import React, { useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { api } from '@/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  FileText, Plus, Users, Send, ChevronLeft, ChevronRight, CheckCircle,
  Clock, Loader2, X, Search, BarChart3, Settings, AlertTriangle, Eye,
  ArrowLeft, Bell, Upload, Download, Brain, RefreshCw, UserCheck, ClipboardList,
  Building2, Briefcase, FileOutput
} from 'lucide-react';

const STATUS_COLORS = {
  requested: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  draft: 'bg-slate-100 text-slate-600',
  submitted: 'bg-cyan-100 text-cyan-700',
  returned: 'bg-red-100 text-red-700',
  resubmitted: 'bg-purple-100 text-purple-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  locked: 'bg-slate-800 text-white',
  overdue: 'bg-red-200 text-red-800',
  active: 'bg-blue-100 text-blue-700',
  all_submitted: 'bg-emerald-100 text-emerald-700',
  generated: 'bg-cyan-100 text-cyan-700',
};

export default function ReportFormView({ report, setReport, onBack, isAdmin, onAI, onReview, projectId }) {
  const [currentSection, setCurrentSection] = useState(0);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showAutoPopulate, setShowAutoPopulate] = useState(false);
  const [autoPopData, setAutoPopData] = useState(null);
  const [autoPopLoading, setAutoPopLoading] = useState(false);

  const sections = report.sections || {};
  const isLocked = ['locked', 'accepted'].includes(report.status);
  const isOwner = true;

  const REPORT_SECTIONS = [
    { id: 'executive_summary', label: 'Executive Summary', icon: FileText },
    { id: 'progress', label: 'Progress', icon: BarChart3 },
    { id: 'drawings', label: 'Drawings & Submissions', icon: FileOutput },
    { id: 'issues', label: 'Issues & Constraints', icon: AlertTriangle },
    { id: 'urgent_items', label: 'Urgent Items', icon: Bell },
    { id: 'tasks_completed', label: 'Tasks Completed', icon: CheckCircle },
    { id: 'outstanding_tasks', label: 'Outstanding Tasks', icon: Clock },
    { id: 'rfis', label: 'RFIs', icon: ClipboardList },
    { id: 'site_instructions', label: 'Site Instructions', icon: Briefcase },
    { id: 'delays', label: 'Delays & Disruptions', icon: AlertTriangle },
    { id: 'quality', label: 'Quality & Snagging', icon: CheckCircle },
    { id: 'commercial', label: 'Commercial', icon: Building2 },
    { id: 'hse', label: 'HSE', icon: UserCheck },
    { id: 'additional_notes', label: 'Additional Notes', icon: FileText },
    { id: 'attachments', label: 'Attachments', icon: Upload },
  ];

  const saveSection = async (sectionId, data) => {
    setSaving(true);
    try {
      await api.put(`/periodic-reports/report/${report.id}/section`, {
        section_id: sectionId,
        data
      });
    } catch (e) { toast.error('Failed to save'); }
    setSaving(false);
  };

  const updateField = (sectionId, field, value) => {
    const sectionData = { ...(sections[sectionId] || {}), [field]: value };
    setReport({ ...report, sections: { ...sections, [sectionId]: sectionData } });
    clearTimeout(window._reportSaveTimer);
    window._reportSaveTimer = setTimeout(() => saveSection(sectionId, sectionData), 1500);
  };

  const addTableRow = (sectionId, listKey) => {
    const sectionData = { ...(sections[sectionId] || {}) };
    const list = [...(sectionData[listKey] || []), {}];
    sectionData[listKey] = list;
    setReport({ ...report, sections: { ...sections, [sectionId]: sectionData } });
  };

  const updateTableRow = (sectionId, listKey, index, field, value) => {
    const sectionData = { ...(sections[sectionId] || {}) };
    const list = [...(sectionData[listKey] || [])];
    list[index] = { ...list[index], [field]: value };
    sectionData[listKey] = list;
    setReport({ ...report, sections: { ...sections, [sectionId]: sectionData } });
    clearTimeout(window._reportSaveTimer);
    window._reportSaveTimer = setTimeout(() => saveSection(sectionId, sectionData), 2000);
  };

  const removeTableRow = (sectionId, listKey, index) => {
    const sectionData = { ...(sections[sectionId] || {}) };
    sectionData[listKey] = (sectionData[listKey] || []).filter((_, i) => i !== index);
    setReport({ ...report, sections: { ...sections, [sectionId]: sectionData } });
    saveSection(sectionId, sectionData);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/periodic-reports/report/${report.id}/submit`);
      toast.success('Report submitted successfully');
      onBack();
    } catch (e) { toast.error('Failed to submit'); }
    setSubmitting(false);
  };

  const handleFileUpload = async (e, section = 'general') => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('section', section);
      formData.append('tag', 'document');
      const r = await api.post(`/periodic-reports/report/${report.id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setReport({ ...report, attachments: [...(report.attachments || []), r.data] });
      toast.success('File uploaded');
    } catch (e) { toast.error('Upload failed'); }
    setUploadingFile(false);
  };

  const deleteAttachment = async (attId) => {
    try {
      await api.delete(`/periodic-reports/report/${report.id}/attachment/${attId}`);
      setReport({ ...report, attachments: (report.attachments || []).filter(a => a.id !== attId) });
      toast.success('Attachment removed');
    } catch (e) { toast.error('Failed to remove'); }
  };

  const loadAutoPopData = async () => {
    setAutoPopLoading(true);
    try {
      const r = await api.get(`/periodic-reports/auto-populate/${projectId}?period_start=${report.period_start || ''}&period_end=${report.period_end || ''}`);
      setAutoPopData(r.data);
      setShowAutoPopulate(true);
    } catch (e) { toast.error('Failed to load project data'); }
    setAutoPopLoading(false);
  };

  const applyAutoPopulate = (sectionId, field, value) => {
    updateField(sectionId, field, value);
    toast.success(`Applied to ${sectionId}`);
  };

  const handlePrint = () => {
    window.print();
  };

  const currentSec = REPORT_SECTIONS[currentSection];
  const secData = sections[currentSec?.id] || {};

  return (
    <DashboardLayout>
      <style>{`
        @media print {
          nav, header, [data-testid="sidebar"], button, .no-print { display: none !important; }
          .print-only { display: block !important; }
          body { background: white !important; }
          * { break-inside: avoid; }
        }
      `}</style>
      <div className="max-w-5xl mx-auto space-y-4">
        {/* Print Header */}
        <div className="hidden print:!block print-only mb-4 border-b-2 border-slate-800 pb-4">
          <h1 className="text-xl font-bold">PERIODIC REPORT</h1>
          <p className="text-sm">{report.company_name} — {report.discipline}</p>
          <p className="text-sm">Period: {report.period_start} to {report.period_end}</p>
          <p className="text-xs text-slate-500">Submitted: {report.submitted_at ? new Date(report.submitted_at).toLocaleDateString() : 'Draft'} | Revision: {report.revision_number}</p>
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="h-4 w-4" /></Button>
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-bold text-slate-900 truncate">Report — {report.company_name}</h1>
              <p className="text-xs text-slate-500">{report.period_start} to {report.period_end} | {report.discipline} | <Badge className={`text-[10px] ${STATUS_COLORS[report.status] || ''}`}>{report.status?.replace(/_/g, ' ')}</Badge></p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {saving && <span className="text-xs text-slate-400 self-center">Saving...</span>}
            <Button variant="outline" size="sm" onClick={loadAutoPopData} disabled={autoPopLoading} data-testid="auto-populate-btn">
              <RefreshCw className={`h-4 w-4 mr-1 ${autoPopLoading ? 'animate-spin' : ''}`} /> Auto-Populate
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-report-btn">
              <FileOutput className="h-4 w-4 mr-1" /> Print
            </Button>
            <Button variant="outline" size="sm" onClick={() => {
              const link = document.createElement('a');
              link.href = `${process.env.REACT_APP_BACKEND_URL}/api/periodic-reports/report/${report.id}/pdf`;
              link.download = 'Periodic_Report.pdf';
              link.click();
            }} data-testid="download-report-pdf-btn">
              <Download className="h-4 w-4 mr-1" /> PDF
            </Button>
            {!isLocked && isOwner && <Button onClick={handleSubmit} disabled={submitting} className="bg-cyan-600 hover:bg-cyan-700" data-testid="submit-report-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Submit Report
            </Button>}
            {isAdmin && <Button variant="outline" size="sm" onClick={() => onAI(report.id)}><Brain className="h-4 w-4 mr-1" /> AI Analyse</Button>}
          </div>
        </div>

        {/* Return comments */}
        {report.return_comments?.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-red-700 mb-1">Returned for Revision</p>
            {report.return_comments.map((c, i) => (
              <p key={i} className="text-xs text-red-600">{c.reason} — {c.by} ({new Date(c.at).toLocaleDateString()})</p>
            ))}
          </div>
        )}

        {/* Section tabs */}
        <div className="flex gap-0.5 overflow-x-auto pb-1">
          {REPORT_SECTIONS.map((sec, i) => {
            const Icon = sec.icon;
            const isActive = i === currentSection;
            return (
              <button key={sec.id} onClick={() => setCurrentSection(i)}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium whitespace-nowrap transition-colors ${isActive ? 'bg-cyan-600 text-white' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'}`}
                data-testid={`section-tab-${sec.id}`}>
                <Icon className="h-3 w-3" /> {sec.label}
              </button>
            );
          })}
        </div>

        {/* Section Content */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2">{currentSec && <currentSec.icon className="h-4 w-4 text-cyan-600" />}{currentSec?.label}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {/* Executive Summary */}
            {currentSec?.id === 'executive_summary' && (
              <>
                <div><Label className="text-xs">Summary of this reporting period</Label>
                  <Textarea value={secData.summary || ''} onChange={e => updateField('executive_summary', 'summary', e.target.value)} disabled={isLocked} rows={3} placeholder="Provide a summary of this reporting period..." /></div>
                <div><Label className="text-xs">Key Progress Achieved</Label>
                  <Textarea value={secData.key_progress || ''} onChange={e => updateField('executive_summary', 'key_progress', e.target.value)} disabled={isLocked} rows={3} /></div>
                <div><Label className="text-xs">Main Concerns</Label>
                  <Textarea value={secData.main_concerns || ''} onChange={e => updateField('executive_summary', 'main_concerns', e.target.value)} disabled={isLocked} rows={3} /></div>
                <div><Label className="text-xs">Urgent Actions Needed</Label>
                  <Textarea value={secData.urgent_actions || ''} onChange={e => updateField('executive_summary', 'urgent_actions', e.target.value)} disabled={isLocked} rows={3} /></div>
              </>
            )}

            {/* Progress */}
            {currentSec?.id === 'progress' && (
              <>
                <div><Label className="text-xs">Description of works/services progressed</Label>
                  <Textarea value={secData.description || ''} onChange={e => updateField('progress', 'description', e.target.value)} disabled={isLocked} rows={4} /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label className="text-xs">Percentage Progress</Label><Input value={secData.percentage || ''} onChange={e => updateField('progress', 'percentage', e.target.value)} disabled={isLocked} /></div>
                  <div><Label className="text-xs">Milestones Achieved</Label><Input value={secData.milestones || ''} onChange={e => updateField('progress', 'milestones', e.target.value)} disabled={isLocked} /></div>
                </div>
                <div><Label className="text-xs">Planned vs Actual Commentary</Label>
                  <Textarea value={secData.planned_vs_actual || ''} onChange={e => updateField('progress', 'planned_vs_actual', e.target.value)} disabled={isLocked} rows={3} /></div>
                <div><Label className="text-xs">Areas/Sections/Packages Progressed</Label>
                  <Textarea value={secData.areas_progressed || ''} onChange={e => updateField('progress', 'areas_progressed', e.target.value)} disabled={isLocked} rows={2} /></div>
                <div>
                  <div className="flex items-center justify-between mb-2"><Label className="text-xs font-semibold">Progress Table</Label>
                    {!isLocked && <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={() => addTableRow('progress', 'table')}><Plus className="h-3 w-3 mr-1" /> Add Row</Button>}
                  </div>
                  {(secData.table || []).map((row, i) => (
                    <div key={i} className="grid grid-cols-5 gap-2 mb-1">
                      <Input className="h-7 text-xs" placeholder="Item" value={row.item || ''} onChange={e => updateTableRow('progress', 'table', i, 'item', e.target.value)} disabled={isLocked} />
                      <Input className="h-7 text-xs" placeholder="Planned" value={row.planned_status || ''} onChange={e => updateTableRow('progress', 'table', i, 'planned_status', e.target.value)} disabled={isLocked} />
                      <Input className="h-7 text-xs" placeholder="Actual" value={row.actual_status || ''} onChange={e => updateTableRow('progress', 'table', i, 'actual_status', e.target.value)} disabled={isLocked} />
                      <Input className="h-7 text-xs" placeholder="Comments" value={row.comments || ''} onChange={e => updateTableRow('progress', 'table', i, 'comments', e.target.value)} disabled={isLocked} />
                      {!isLocked && <Button variant="ghost" size="sm" className="h-7" onClick={() => removeTableRow('progress', 'table', i)}><X className="h-3 w-3 text-red-500" /></Button>}
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Drawings */}
            {currentSec?.id === 'drawings' && (
              <>
                <div><Label className="text-xs">Drawings Submitted This Period</Label>
                  <Textarea value={secData.submitted || ''} onChange={e => updateField('drawings', 'submitted', e.target.value)} disabled={isLocked} rows={3} /></div>
                <div><Label className="text-xs">Revisions Received</Label>
                  <Textarea value={secData.revisions_received || ''} onChange={e => updateField('drawings', 'revisions_received', e.target.value)} disabled={isLocked} rows={2} /></div>
                <div><Label className="text-xs">Information Still Outstanding</Label>
                  <Textarea value={secData.outstanding || ''} onChange={e => updateField('drawings', 'outstanding', e.target.value)} disabled={isLocked} rows={2} /></div>
                <div>
                  <div className="flex items-center justify-between mb-2"><Label className="text-xs font-semibold">Drawings Register</Label>
                    {!isLocked && <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={() => addTableRow('drawings', 'table')}><Plus className="h-3 w-3 mr-1" /> Add</Button>}
                  </div>
                  {(secData.table || []).map((row, i) => (
                    <div key={i} className="grid grid-cols-4 gap-2 mb-1">
                      <Input className="h-7 text-xs" placeholder="Reference" value={row.reference || ''} onChange={e => updateTableRow('drawings', 'table', i, 'reference', e.target.value)} disabled={isLocked} />
                      <Input className="h-7 text-xs" placeholder="Description" value={row.description || ''} onChange={e => updateTableRow('drawings', 'table', i, 'description', e.target.value)} disabled={isLocked} />
                      <Input className="h-7 text-xs" placeholder="Status" value={row.status || ''} onChange={e => updateTableRow('drawings', 'table', i, 'status', e.target.value)} disabled={isLocked} />
                      {!isLocked && <Button variant="ghost" size="sm" className="h-7" onClick={() => removeTableRow('drawings', 'table', i)}><X className="h-3 w-3 text-red-500" /></Button>}
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Issues */}
            {currentSec?.id === 'issues' && (
              <>
                <div className="flex items-center justify-between mb-2"><Label className="text-xs font-semibold">Issues & Constraints</Label>
                  {!isLocked && <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={() => addTableRow('issues', 'entries')}><Plus className="h-3 w-3 mr-1" /> Add Issue</Button>}
                </div>
                {(secData.entries || []).map((entry, i) => (
                  <div key={i} className="border rounded-lg p-3 space-y-2 mb-2">
                    <div className="flex justify-between items-center">
                      <Input className="h-7 text-xs flex-1 mr-2" placeholder="Issue Title" value={entry.title || ''} onChange={e => updateTableRow('issues', 'entries', i, 'title', e.target.value)} disabled={isLocked} />
                      <Select value={entry.urgency || 'medium'} onValueChange={v => updateTableRow('issues', 'entries', i, 'urgency', v)} disabled={isLocked}>
                        <SelectTrigger className="w-28 h-7 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="critical">Critical</SelectItem></SelectContent>
                      </Select>
                      {!isLocked && <Button variant="ghost" size="sm" onClick={() => removeTableRow('issues', 'entries', i)}><X className="h-3 w-3 text-red-500" /></Button>}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input className="h-7 text-xs" placeholder="Category" value={entry.category || ''} onChange={e => updateTableRow('issues', 'entries', i, 'category', e.target.value)} disabled={isLocked} />
                      <Input className="h-7 text-xs" placeholder="Affected Area" value={entry.affected_area || ''} onChange={e => updateTableRow('issues', 'entries', i, 'affected_area', e.target.value)} disabled={isLocked} />
                    </div>
                    <Textarea className="text-xs" placeholder="Description" value={entry.description || ''} onChange={e => updateTableRow('issues', 'entries', i, 'description', e.target.value)} disabled={isLocked} rows={2} />
                    <div className="grid grid-cols-2 gap-2">
                      <Input className="h-7 text-xs" placeholder="Responsible Party" value={entry.responsible_party || ''} onChange={e => updateTableRow('issues', 'entries', i, 'responsible_party', e.target.value)} disabled={isLocked} />
                      <Input className="h-7 text-xs" type="date" placeholder="Target Date" value={entry.target_date || ''} onChange={e => updateTableRow('issues', 'entries', i, 'target_date', e.target.value)} disabled={isLocked} />
                    </div>
                  </div>
                ))}
                {(secData.entries || []).length === 0 && <p className="text-xs text-slate-400 text-center py-4">No issues reported. Click "Add Issue" to start.</p>}
              </>
            )}

            {/* Urgent Items */}
            {currentSec?.id === 'urgent_items' && (
              <>
                <div className="flex items-center justify-between mb-2"><Label className="text-xs font-semibold">Urgent Items / Decisions Required</Label>
                  {!isLocked && <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={() => addTableRow('urgent_items', 'entries')}><Plus className="h-3 w-3 mr-1" /> Add</Button>}
                </div>
                {(secData.entries || []).map((entry, i) => (
                  <div key={i} className="border border-red-200 bg-red-50/30 rounded-lg p-3 space-y-2 mb-2">
                    <div className="flex justify-between">
                      <Input className="h-7 text-xs flex-1 mr-2" placeholder="Urgent Item" value={entry.item || ''} onChange={e => updateTableRow('urgent_items', 'entries', i, 'item', e.target.value)} disabled={isLocked} />
                      {!isLocked && <Button variant="ghost" size="sm" onClick={() => removeTableRow('urgent_items', 'entries', i)}><X className="h-3 w-3 text-red-500" /></Button>}
                    </div>
                    <Textarea className="text-xs" placeholder="Why is this urgent?" value={entry.why_urgent || ''} onChange={e => updateTableRow('urgent_items', 'entries', i, 'why_urgent', e.target.value)} disabled={isLocked} rows={2} />
                    <div className="grid grid-cols-3 gap-2">
                      <Input className="h-7 text-xs" type="date" placeholder="Deadline" value={entry.deadline || ''} onChange={e => updateTableRow('urgent_items', 'entries', i, 'deadline', e.target.value)} disabled={isLocked} />
                      <Input className="h-7 text-xs" placeholder="Who must act" value={entry.who_must_act || ''} onChange={e => updateTableRow('urgent_items', 'entries', i, 'who_must_act', e.target.value)} disabled={isLocked} />
                      <Input className="h-7 text-xs" placeholder="Consequence if not resolved" value={entry.consequence || ''} onChange={e => updateTableRow('urgent_items', 'entries', i, 'consequence', e.target.value)} disabled={isLocked} />
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Tasks Completed / Outstanding Tasks */}
            {(currentSec?.id === 'tasks_completed' || currentSec?.id === 'outstanding_tasks') && (
              <>
                <div className="flex items-center justify-between mb-2"><Label className="text-xs font-semibold">{currentSec.label}</Label>
                  {!isLocked && <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={() => addTableRow(currentSec.id, 'entries')}><Plus className="h-3 w-3 mr-1" /> Add</Button>}
                </div>
                {(secData.entries || []).map((entry, i) => (
                  <div key={i} className="grid grid-cols-4 gap-2 mb-1">
                    <Input className="h-7 text-xs col-span-2" placeholder="Task title" value={entry.title || ''} onChange={e => updateTableRow(currentSec.id, 'entries', i, 'title', e.target.value)} disabled={isLocked} />
                    <Input className="h-7 text-xs" placeholder="Status / Due" value={entry.status || ''} onChange={e => updateTableRow(currentSec.id, 'entries', i, 'status', e.target.value)} disabled={isLocked} />
                    {!isLocked && <Button variant="ghost" size="sm" className="h-7" onClick={() => removeTableRow(currentSec.id, 'entries', i)}><X className="h-3 w-3 text-red-500" /></Button>}
                  </div>
                ))}
              </>
            )}

            {/* RFIs */}
            {currentSec?.id === 'rfis' && (
              <>
                <div className="grid grid-cols-3 gap-4">
                  <div><Label className="text-xs">RFIs Raised This Period</Label><Input value={secData.raised || ''} onChange={e => updateField('rfis', 'raised', e.target.value)} disabled={isLocked} /></div>
                  <div><Label className="text-xs">RFIs Answered</Label><Input value={secData.answered || ''} onChange={e => updateField('rfis', 'answered', e.target.value)} disabled={isLocked} /></div>
                  <div><Label className="text-xs">RFIs Outstanding</Label><Input value={secData.outstanding || ''} onChange={e => updateField('rfis', 'outstanding', e.target.value)} disabled={isLocked} /></div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2"><Label className="text-xs font-semibold">RFI Register</Label>
                    {!isLocked && <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={() => addTableRow('rfis', 'table')}><Plus className="h-3 w-3 mr-1" /> Add</Button>}
                  </div>
                  {(secData.table || []).map((row, i) => (
                    <div key={i} className="grid grid-cols-5 gap-2 mb-1">
                      <Input className="h-7 text-xs" placeholder="RFI No" value={row.rfi_number || ''} onChange={e => updateTableRow('rfis', 'table', i, 'rfi_number', e.target.value)} disabled={isLocked} />
                      <Input className="h-7 text-xs" placeholder="Title" value={row.title || ''} onChange={e => updateTableRow('rfis', 'table', i, 'title', e.target.value)} disabled={isLocked} />
                      <Input className="h-7 text-xs" placeholder="Status" value={row.status || ''} onChange={e => updateTableRow('rfis', 'table', i, 'status', e.target.value)} disabled={isLocked} />
                      <Input className="h-7 text-xs" placeholder="Comments" value={row.comments || ''} onChange={e => updateTableRow('rfis', 'table', i, 'comments', e.target.value)} disabled={isLocked} />
                      {!isLocked && <Button variant="ghost" size="sm" className="h-7" onClick={() => removeTableRow('rfis', 'table', i)}><X className="h-3 w-3 text-red-500" /></Button>}
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Site Instructions */}
            {currentSec?.id === 'site_instructions' && (
              <>
                <div><Label className="text-xs">SIs Issued / Received</Label>
                  <Textarea value={secData.issued || ''} onChange={e => updateField('site_instructions', 'issued', e.target.value)} disabled={isLocked} rows={3} /></div>
                <div><Label className="text-xs">Variation-Related Instructions</Label>
                  <Textarea value={secData.variations || ''} onChange={e => updateField('site_instructions', 'variations', e.target.value)} disabled={isLocked} rows={2} /></div>
                <div><Label className="text-xs">Unresolved Instructions</Label>
                  <Textarea value={secData.unresolved || ''} onChange={e => updateField('site_instructions', 'unresolved', e.target.value)} disabled={isLocked} rows={2} /></div>
              </>
            )}

            {/* Delays */}
            {currentSec?.id === 'delays' && (
              <>
                <div className="flex items-center justify-between mb-2"><Label className="text-xs font-semibold">Delays & Disruptions</Label>
                  {!isLocked && <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={() => addTableRow('delays', 'entries')}><Plus className="h-3 w-3 mr-1" /> Add</Button>}
                </div>
                {(secData.entries || []).map((entry, i) => (
                  <div key={i} className="border rounded-lg p-3 space-y-2 mb-2">
                    <div className="flex justify-between"><Input className="h-7 text-xs flex-1 mr-2" placeholder="Delay description" value={entry.description || ''} onChange={e => updateTableRow('delays', 'entries', i, 'description', e.target.value)} disabled={isLocked} />
                      {!isLocked && <Button variant="ghost" size="sm" onClick={() => removeTableRow('delays', 'entries', i)}><X className="h-3 w-3 text-red-500" /></Button>}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <Input className="h-7 text-xs" placeholder="Cause" value={entry.cause || ''} onChange={e => updateTableRow('delays', 'entries', i, 'cause', e.target.value)} disabled={isLocked} />
                      <Input className="h-7 text-xs" placeholder="Affected Section" value={entry.affected_section || ''} onChange={e => updateTableRow('delays', 'entries', i, 'affected_section', e.target.value)} disabled={isLocked} />
                      <Input className="h-7 text-xs" placeholder="Time Impact" value={entry.time_impact || ''} onChange={e => updateTableRow('delays', 'entries', i, 'time_impact', e.target.value)} disabled={isLocked} />
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Quality */}
            {currentSec?.id === 'quality' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-xs">Inspections Carried Out</Label><Textarea value={secData.inspections || ''} onChange={e => updateField('quality', 'inspections', e.target.value)} disabled={isLocked} rows={2} /></div>
                  <div><Label className="text-xs">Defects Noted</Label><Textarea value={secData.defects || ''} onChange={e => updateField('quality', 'defects', e.target.value)} disabled={isLocked} rows={2} /></div>
                  <div><Label className="text-xs">Snags Closed</Label><Textarea value={secData.snags_closed || ''} onChange={e => updateField('quality', 'snags_closed', e.target.value)} disabled={isLocked} rows={2} /></div>
                  <div><Label className="text-xs">Snags Outstanding</Label><Textarea value={secData.snags_outstanding || ''} onChange={e => updateField('quality', 'snags_outstanding', e.target.value)} disabled={isLocked} rows={2} /></div>
                </div>
              </>
            )}

            {/* Commercial */}
            {currentSec?.id === 'commercial' && (
              <>
                <div><Label className="text-xs">Potential VO Implications</Label><Textarea value={secData.vo_implications || ''} onChange={e => updateField('commercial', 'vo_implications', e.target.value)} disabled={isLocked} rows={3} /></div>
                <div><Label className="text-xs">Cost Concerns</Label><Textarea value={secData.cost_concerns || ''} onChange={e => updateField('commercial', 'cost_concerns', e.target.value)} disabled={isLocked} rows={2} /></div>
                <div><Label className="text-xs">Budget Risk</Label><Textarea value={secData.budget_risk || ''} onChange={e => updateField('commercial', 'budget_risk', e.target.value)} disabled={isLocked} rows={2} /></div>
              </>
            )}

            {/* HSE */}
            {currentSec?.id === 'hse' && (
              <>
                <div><Label className="text-xs">Incidents Affecting Progress</Label><Textarea value={secData.incidents || ''} onChange={e => updateField('hse', 'incidents', e.target.value)} disabled={isLocked} rows={2} /></div>
                <div><Label className="text-xs">Access Restrictions</Label><Textarea value={secData.restrictions || ''} onChange={e => updateField('hse', 'restrictions', e.target.value)} disabled={isLocked} rows={2} /></div>
                <div><Label className="text-xs">Permit Issues</Label><Textarea value={secData.permits || ''} onChange={e => updateField('hse', 'permits', e.target.value)} disabled={isLocked} rows={2} /></div>
                <div><Label className="text-xs">Environmental Events</Label><Textarea value={secData.environmental || ''} onChange={e => updateField('hse', 'environmental', e.target.value)} disabled={isLocked} rows={2} /></div>
              </>
            )}

            {/* Additional Notes */}
            {currentSec?.id === 'additional_notes' && (
              <div><Label className="text-xs">Additional Notes</Label>
                <Textarea value={secData.notes || ''} onChange={e => updateField('additional_notes', 'notes', e.target.value)} disabled={isLocked} rows={6} placeholder="Any additional information not covered in other sections..." /></div>
            )}

            {/* Attachments */}
            {currentSec?.id === 'attachments' && (
              <>
                {!isLocked && (
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                    <Upload className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                    <p className="text-sm text-slate-500 mb-2">Drop files here or click to upload</p>
                    <input type="file" onChange={handleFileUpload} className="hidden" id="report-file-upload" />
                    <label htmlFor="report-file-upload">
                      <Button variant="outline" size="sm" asChild><span>{uploadingFile ? 'Uploading...' : 'Choose File'}</span></Button>
                    </label>
                  </div>
                )}
                <div className="space-y-2">{(report.attachments || []).map(att => (
                  <div key={att.id} className="flex items-center justify-between border rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-slate-400" />
                      <div>
                        <p className="text-xs font-medium">{att.filename}</p>
                        <p className="text-[10px] text-slate-400">{att.section} • {(att.size / 1024).toFixed(1)} KB</p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${att.url}`} target="_blank" rel="noopener noreferrer"><Button variant="ghost" size="sm" className="h-6"><Download className="h-3 w-3" /></Button></a>
                      {!isLocked && <Button variant="ghost" size="sm" className="h-6" onClick={() => deleteAttachment(att.id)}><X className="h-3 w-3 text-red-500" /></Button>}
                    </div>
                  </div>
                ))}</div>
                {(report.attachments || []).length === 0 && <p className="text-xs text-slate-400 text-center py-4">No attachments yet.</p>}
              </>
            )}
          </CardContent>
        </Card>

        {/* AI Analysis */}
        {report.ai_analysis && (
          <Card className="border-purple-200" data-testid="report-ai-analysis">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Brain className="h-4 w-4 text-purple-600" /> AI Analysis</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              {report.ai_analysis.summary && <div className="bg-purple-50 rounded-lg p-3 border border-purple-100"><p className="font-semibold text-purple-800 mb-1">Summary</p><p className="text-slate-700 text-xs">{report.ai_analysis.summary}</p></div>}
              {report.ai_analysis.risks?.length > 0 && <div><p className="font-semibold text-red-700 mb-1">Key Risks</p><ul className="list-disc pl-5 text-xs text-slate-600">{report.ai_analysis.risks.map((r, i) => <li key={i}>{typeof r === 'string' ? r : r.risk || JSON.stringify(r)}</li>)}</ul></div>}
              {report.ai_analysis.urgent_items?.length > 0 && <div><p className="font-semibold text-red-700 mb-1">Urgent Items</p><ul className="list-disc pl-5 text-xs text-slate-600">{report.ai_analysis.urgent_items.map((u, i) => <li key={i}>{typeof u === 'string' ? u : JSON.stringify(u)}</li>)}</ul></div>}
              {report.ai_analysis.delay_risks?.length > 0 && <div><p className="font-semibold text-amber-700 mb-1">Delay Risks</p><ul className="list-disc pl-5 text-xs text-slate-600">{report.ai_analysis.delay_risks.map((d, i) => <li key={i}>{typeof d === 'string' ? d : JSON.stringify(d)}</li>)}</ul></div>}
              {report.ai_analysis.missing_sections?.length > 0 && <div><p className="font-semibold text-slate-700 mb-1">Missing/Incomplete Sections</p><ul className="list-disc pl-5 text-xs text-slate-500">{report.ai_analysis.missing_sections.map((m, i) => <li key={i}>{typeof m === 'string' ? m : JSON.stringify(m)}</li>)}</ul></div>}
              {report.ai_analysis.recommended_actions?.length > 0 && <div><p className="font-semibold text-cyan-700 mb-1">Recommended Actions</p><ul className="list-disc pl-5 text-xs text-slate-600">{report.ai_analysis.recommended_actions.map((a, i) => <li key={i}>{typeof a === 'string' ? a : a.action || JSON.stringify(a)}</li>)}</ul></div>}
            </CardContent>
          </Card>
        )}

        {/* Auto-Populate Panel */}
        {showAutoPopulate && autoPopData && (
          <Card className="border-cyan-200 bg-cyan-50/20" data-testid="auto-populate-panel">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2"><RefreshCw className="h-4 w-4 text-cyan-600" /> Smart Auto-Populate — Project Data</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowAutoPopulate(false)}><X className="h-4 w-4" /></Button>
              </div>
              <p className="text-[10px] text-slate-500">Click "Apply" to pre-fill report sections with project data from {report.period_start} to {report.period_end}</p>
            </CardHeader>
            <CardContent className="space-y-3 max-h-96 overflow-y-auto">
              {(autoPopData.rfis?.raised?.length > 0 || autoPopData.rfis?.outstanding?.length > 0) && (
                <div className="border rounded-lg p-3 bg-white">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-slate-700">RFIs ({autoPopData.rfis.raised?.length || 0} raised, {autoPopData.rfis.outstanding?.length || 0} outstanding)</p>
                    <Button size="sm" className="h-6 text-[10px] bg-cyan-600" onClick={() => {
                      const raised = autoPopData.rfis.raised?.map(r => `${r.number}: ${r.subject} (${r.status})`).join('\n') || '';
                      const outstanding = autoPopData.rfis.outstanding?.map(r => `${r.number}: ${r.subject}`).join('\n') || '';
                      applyAutoPopulate('rfis', 'raised', raised);
                      applyAutoPopulate('rfis', 'outstanding', outstanding);
                      const table = [...(autoPopData.rfis.raised || []), ...(autoPopData.rfis.outstanding || [])].map(r => ({
                        rfi_number: r.number, title: r.subject, status: r.status, comments: ''
                      }));
                      applyAutoPopulate('rfis', 'table', table);
                    }}>Apply</Button>
                  </div>
                  <div className="text-[10px] text-slate-500 space-y-0.5">{autoPopData.rfis.raised?.slice(0, 5).map((r, i) => <p key={i}>{r.number}: {r.subject} ({r.status})</p>)}</div>
                </div>
              )}
              {(autoPopData.tasks?.completed?.length > 0 || autoPopData.tasks?.outstanding?.length > 0) && (
                <div className="border rounded-lg p-3 bg-white">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-slate-700">Tasks ({autoPopData.tasks.completed?.length || 0} completed, {autoPopData.tasks.outstanding?.length || 0} outstanding)</p>
                    <Button size="sm" className="h-6 text-[10px] bg-cyan-600" onClick={() => {
                      const completed = (autoPopData.tasks.completed || []).map(t => ({ title: t.title, status: 'Completed', due: t.due_date }));
                      const outstanding = (autoPopData.tasks.outstanding || []).map(t => ({ title: t.title, status: t.status, due: t.due_date }));
                      applyAutoPopulate('tasks_completed', 'entries', completed);
                      applyAutoPopulate('outstanding_tasks', 'entries', outstanding);
                    }}>Apply</Button>
                  </div>
                  <div className="text-[10px] text-slate-500 space-y-0.5">{autoPopData.tasks.completed?.slice(0, 3).map((t, i) => <p key={i}>{t.title}</p>)}</div>
                </div>
              )}
              {(autoPopData.drawings?.submitted?.length > 0 || autoPopData.drawings?.received?.length > 0) && (
                <div className="border rounded-lg p-3 bg-white">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-slate-700">Drawings ({autoPopData.drawings.submitted?.length || 0} submitted, {autoPopData.drawings.received?.length || 0} shop drawings)</p>
                    <Button size="sm" className="h-6 text-[10px] bg-cyan-600" onClick={() => {
                      const submitted = autoPopData.drawings.submitted?.map(d => `${d.number}: ${d.title} (Rev ${d.revision || '-'})`).join('\n') || '';
                      applyAutoPopulate('drawings', 'submitted', submitted);
                      const table = (autoPopData.drawings.submitted || []).map(d => ({
                        reference: d.number, description: d.title, status: d.status, comments: ''
                      }));
                      applyAutoPopulate('drawings', 'table', table);
                    }}>Apply</Button>
                  </div>
                  <div className="text-[10px] text-slate-500 space-y-0.5">{autoPopData.drawings.submitted?.slice(0, 3).map((d, i) => <p key={i}>{d.number}: {d.title}</p>)}</div>
                </div>
              )}
              {autoPopData.delay_events?.length > 0 && (
                <div className="border rounded-lg p-3 bg-white">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-slate-700">Delay Events ({autoPopData.delay_events.length})</p>
                    <Button size="sm" className="h-6 text-[10px] bg-cyan-600" onClick={() => {
                      const entries = autoPopData.delay_events.map(d => ({
                        description: d.title || d.description, cause: '', affected_section: '', time_impact: d.impact || ''
                      }));
                      applyAutoPopulate('delays', 'entries', entries);
                    }}>Apply</Button>
                  </div>
                  <div className="text-[10px] text-slate-500 space-y-0.5">{autoPopData.delay_events.slice(0, 3).map((d, i) => <p key={i}>{d.title}</p>)}</div>
                </div>
              )}
              {autoPopData.site_instructions?.length > 0 && (
                <div className="border rounded-lg p-3 bg-white">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-slate-700">Site Instructions ({autoPopData.site_instructions.length})</p>
                    <Button size="sm" className="h-6 text-[10px] bg-cyan-600" onClick={() => {
                      const text = autoPopData.site_instructions.map(s => `${s.number}: ${s.subject} (${s.status})`).join('\n');
                      applyAutoPopulate('site_instructions', 'issued', text);
                    }}>Apply</Button>
                  </div>
                  <div className="text-[10px] text-slate-500 space-y-0.5">{autoPopData.site_instructions.slice(0, 3).map((s, i) => <p key={i}>{s.number}: {s.subject}</p>)}</div>
                </div>
              )}
              {Object.values(autoPopData).every(v => Array.isArray(v) ? v.length === 0 : Object.values(v).every(arr => !Array.isArray(arr) || arr.length === 0)) && (
                <p className="text-xs text-slate-400 text-center py-4">No project data found for this period. Data will appear here as RFIs, tasks, drawings, etc. are created.</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex justify-between">
          <Button onClick={() => setCurrentSection(Math.max(0, currentSection - 1))} variant="outline" disabled={currentSection === 0}>
            <ChevronLeft className="h-4 w-4 mr-1" /> Previous
          </Button>
          <span className="text-sm text-slate-400 self-center">Section {currentSection + 1} of {REPORT_SECTIONS.length}</span>
          <Button onClick={() => setCurrentSection(Math.min(REPORT_SECTIONS.length - 1, currentSection + 1))} disabled={currentSection === REPORT_SECTIONS.length - 1}>
            Next <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
